import { createFileRoute, Link } from '@tanstack/react-router';
import { Schema } from 'effect';
import { useEffect } from 'react';
import { useAnalytics } from '@listening/frontend/analytics/client';
import BlueLogo from '@listening/assets/astro/icons/icon-blue-logo.svg?react';
import { AUTH_ERROR_DEFAULT_RETURN } from '@/services/auth-service';
import { optionalCatching } from '@/lib/search-params';
import * as m from '@/paraglide/messages';
import { localizeHref } from '@/paraglide/runtime';
import { seo } from '@/seo/seo';

/**
 * Where a failed OAuth round-trip lands.
 *
 * better-auth only reaches its own `/api/auth/error` when nothing else is
 * configured, and what that does in production is the reason this route
 * exists: it 302s to `/?error=<code>` (`better-auth/dist/api/routes/error.mjs`
 * — the branded HTML error page is non-production only). So every failed
 * Google/Apple sign-in silently dumped the user on the marketing homepage with
 * a query param nothing reads, indistinguishable from them having navigated
 * there on purpose. It was invisible in analytics too.
 *
 * Reached two ways, and both need to keep working:
 *
 * 1. `errorCallbackURL` on the sign-in call (`signInSocial`), which better-auth
 *    stores in the OAuth state so it survives the provider round-trip. This is
 *    the path that carries `return_to`, and it is why a funnel sign-in can come
 *    back to the funnel rather than the app's login page.
 * 2. `onAPIError.errorURL` in the better-auth server config, the global default
 *    for failures that have no per-flow state to read — an expired proxy
 *    payload, `state_not_found`, a callback with no state at all. Those carry
 *    no `return_to`, so the retry falls back to `/app/login`.
 *
 * Deliberately NOT under `_marketing`: that layout's `head()` injects the
 * landing-stage pixels (TikTok/Quora `ViewContent` and friends), and firing a
 * landing conversion signal on a sign-in failure is both wrong and the
 * duplicate-event bug `docs/projects/complete/accounts-and-billing/audit-cookie-trackers.md` Phase 1
 * found on `/post-purchase`. Same reasoning as `_funnel`.
 */
const AuthErrorSearch = Schema.Struct({
  /** better-auth's machine-readable code. Absent if someone opens the URL bare. */
  error: optionalCatching(Schema.String),
  /** Human-ish detail from the provider. Shown to nobody; logged into the event. */
  error_description: optionalCatching(Schema.String),
  /** Where "try again" goes. Validated below — it arrives from the URL. */
  return_to: optionalCatching(Schema.String),
});

export const Route = createFileRoute('/auth-error')({
  validateSearch: Schema.toStandardSchemaV1(AuthErrorSearch),
  head: () => ({
    meta: [
      ...seo({
        title: m.meta_auth_error_title(),
        description: m.meta_auth_error_description(),
      }),
      // A failed sign-in is a dead end, not something to rank or resurface.
      { name: 'robots', content: 'noindex,nofollow' },
    ],
  }),
  component: AuthErrorPage,
});

/**
 * `return_to` reaches us through a URL, so treat it as hostile: anything but a
 * plain same-origin path falls back to the login page. Rejecting `//` and `\\`
 * is the part that matters — `//evil.com` is a protocol-relative URL that a
 * browser resolves to a different origin, which would turn a sign-in failure
 * into an open redirect pointed at whoever crafted the link.
 */
const safeReturnTo = (value: string | undefined) =>
  value && /^\/(?!\/|\\)[\w\-./]*$/.test(value)
    ? value
    : AUTH_ERROR_DEFAULT_RETURN;

/**
 * Codes worth their own sentence. Everything else gets the generic copy —
 * better-auth and the providers between them can emit codes we have never
 * seen, and guessing at wording for those is worse than saying less.
 */
const messageForCode = (code: string | undefined) => {
  // No code at all means someone opened this URL directly rather than being
  // redirected here, so there is nothing specific to say.
  if (!code) return m.auth_error_generic();
  switch (code) {
    // The provider's own code when the user backs out of the consent screen.
    case 'access_denied': {
      return m.auth_error_cancelled();
    }
    case 'account_already_linked_to_different_user':
    case 'email_does_not_match': {
      return m.auth_error_already_linked();
    }
    case 'email_not_found':
    case 'email_not_verified': {
      return m.auth_error_no_email();
    }
    // The `state_*` codes are better-auth's own state checks: parameter
    // mismatch, missing or undecryptable state. All mean a stale round-trip
    // or a dropped cookie, which is what the "expired, try again" copy says.
    case 'payload_expired':
    case 'state_mismatch':
    case 'state_not_found':
    case 'state_security_mismatch':
    case 'state_invalid': {
      return m.auth_error_expired();
    }
    default: {
      return m.auth_error_generic();
    }
  }
};

function AuthErrorPage() {
  const { capture } = useAnalytics();
  const code = Route.useSearch({ select: (s) => s.error });
  const rawReturnTo = Route.useSearch({ select: (s) => s.return_to });
  const returnTo = safeReturnTo(rawReturnTo);

  useEffect(() => {
    // The tab that started the sign-in is gone, so this is the only place a
    // failed social sign-in can be counted at all.
    capture('auth_error', { code: code ?? 'unknown', return_to: returnTo });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on view
  }, [code, returnTo]);

  return (
    <main className='flex min-h-screen flex-col items-center justify-center bg-mist-165 px-4 py-10 font-sans text-neutral-900'>
      <div className='w-full max-w-[440px] rounded-[20px] bg-white p-6 text-center shadow-[0_20px_50px_rgba(8,1,36,0.12)] sm:p-8'>
        <div className='mb-4 flex justify-center'>
          <BlueLogo aria-hidden className='h-7 w-auto' />
        </div>
        <h1 className='mb-2 text-[24px] leading-tight font-extrabold tracking-tight text-balance'>
          {m.auth_error_h()}
        </h1>
        <p className='mb-6 text-[15px] text-steel-700'>
          {messageForCode(code)}
        </p>

        {/*
          A plain anchor, not `<Link>`: `to` is typed against the route tree and
          this destination is only known at runtime. A full page load is also
          the right recovery — we are arriving from an external redirect, so
          there is no client router state worth preserving.
        */}
        <a
          className='inline-flex w-full items-center justify-center rounded-full bg-azure-595 px-6 py-3.5 text-base font-bold text-white shadow-[0_6px_18px_rgba(5,135,251,0.28)] transition-colors hover:bg-azure-700'
          href={localizeHref(returnTo)}
        >
          {m.auth_error_retry()}
        </a>

        <div className='mt-4'>
          <Link
            className='text-[13.5px] font-semibold text-azure-455 hover:underline'
            to='/'
          >
            {m.auth_error_home()}
          </Link>
        </div>

        {code && (
          <div className='mt-6 text-[12px] text-steel-700'>
            {m.auth_error_code_label()}:{' '}
            <code className='font-mono break-all'>{code}</code>
          </div>
        )}
      </div>
    </main>
  );
}
